import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { SidebarMenuEditor } from '@/components/plans/SidebarMenuEditor';

const AVAILABLE_FEATURES = [
  'chatbot',
  'api_access',
  'advanced_kpis',
  'export',
  'dashboard',
  'reports',
  'forecast',
  'budget',
  'donaciones',
  'email',
  'erp',
];

interface PlanForm {
  name: string;
  description: string;
  maxUsers: number;
  maxChannels: number;
  priceMonthly: number;
  features: string[];
  sidebarMenu: string[] | null;
  isActive: boolean;
}

const EMPTY_FORM: PlanForm = {
  name: '',
  description: '',
  maxUsers: 5,
  maxChannels: 3,
  priceMonthly: 0,
  features: [],
  sidebarMenu: null,
  isActive: true,
};

function parseArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string');
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : [];
    } catch {
      return [];
    }
  }
  return [];
}

function authHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
  };
}

export default function PlanFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const [formData, setFormData] = useState<PlanForm>(EMPTY_FORM);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/super-admin/plans/${id}`, { headers: authHeaders() });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Plan no encontrado');
        if (cancelled) return;
        const plan = data.data;
        setFormData({
          name: plan.name ?? '',
          description: plan.description ?? '',
          maxUsers: Number(plan.maxUsers ?? 5),
          maxChannels: Number(plan.maxChannels ?? 3),
          priceMonthly: Number(plan.priceMonthly ?? 0),
          features: parseArray(plan.features),
          sidebarMenu: plan.sidebarMenu == null ? null : parseArray(plan.sidebarMenu),
          isActive: plan.isActive !== false,
        });
      } catch (err: any) {
        if (!cancelled) {
          setNotFound(true);
          setError(err.message || 'No se pudo cargar el plan');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const toggleFeature = (feature: string) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter((item) => item !== feature)
        : [...prev.features, feature],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    const payload = {
      name: formData.name,
      description: formData.description,
      features: formData.features,
      isActive: formData.isActive,
      integrations: [],
      ...(formData.sidebarMenu ? { sidebarMenu: formData.sidebarMenu } : {}),
      maxUsers: Number(formData.maxUsers),
      maxChannels: Number(formData.maxChannels),
      priceMonthly: Number(formData.priceMonthly),
    };

    try {
      const res = await fetch(isNew ? '/api/super-admin/plans' : `/api/super-admin/plans/${id}`, {
        method: isNew ? 'POST' : 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar el plan');
      navigate('/plans');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-lyrax-primary" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="text-center py-12">
        <p className="text-lyrax-text-secondary">{error || 'Plan no encontrado'}</p>
        <Link to="/plans" className="text-lyrax-primary hover:underline mt-2 inline-block">
          Volver a planes
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <Link
        to="/plans"
        className="inline-flex items-center gap-2 text-lyrax-text-secondary hover:text-lyrax-primary mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Volver a planes
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-lyrax-text">{isNew ? 'Nuevo plan' : 'Editar plan'}</h1>
          <p className="text-lyrax-text-secondary">
            El menú del sidebar se define aquí. Las licencias no cambian lo que se ve en el menú.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/plans" className="btn-secondary">
            Cancelar
          </Link>
          <button type="submit" className="btn-primary flex items-center gap-2" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {isSubmitting ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>

      {error && (
        <div
          className="mb-6 p-3 rounded-lyrax border border-red-200 bg-red-50 text-sm text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200"
          role="alert"
        >
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[22rem_1fr] gap-6 items-start">
        <div className="card space-y-4">
          <h2 className="text-sm font-semibold text-lyrax-text">Datos del plan</h2>
          <div>
            <label htmlFor="plan-name" className="label">Nombre del plan</label>
            <input
              id="plan-name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              className="input"
              placeholder="Plan Estándar"
              required
            />
          </div>
          <div>
            <label htmlFor="plan-price" className="label">Precio mensual (CLP)</label>
            <input
              id="plan-price"
              name="priceMonthly"
              type="number"
              min="0"
              value={formData.priceMonthly}
              onChange={handleChange}
              className="input"
            />
          </div>
          <div>
            <label htmlFor="plan-desc" className="label">Descripción</label>
            <textarea
              id="plan-desc"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="input min-h-[88px] resize-y"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="plan-users" className="label">Máximo de usuarios</label>
              <input
                id="plan-users"
                name="maxUsers"
                type="number"
                min="0"
                value={formData.maxUsers}
                onChange={handleChange}
                className="input"
              />
              <p className="mt-1 text-sm text-lyrax-text-secondary">0 = sin límite</p>
            </div>
            <div>
              <label htmlFor="plan-channels" className="label">Máximo de canales</label>
              <input
                id="plan-channels"
                name="maxChannels"
                type="number"
                min="0"
                value={formData.maxChannels}
                onChange={handleChange}
                className="input"
              />
              <p className="mt-1 text-sm text-lyrax-text-secondary">0 = sin límite</p>
            </div>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              name="isActive"
              type="checkbox"
              checked={formData.isActive}
              onChange={handleChange}
              className="w-4 h-4 rounded border-lyrax-border text-lyrax-primary focus:ring-lyrax-primary"
            />
            <span className="text-sm font-medium text-lyrax-text">Plan activo</span>
          </label>

          <div>
            <p className="label">Licencias</p>
            <p className="mb-2 text-sm text-lyrax-text-secondary">
              Capacidades del producto. No agregan ni quitan entradas del menú.
            </p>
            <div className="flex flex-wrap gap-2">
              {AVAILABLE_FEATURES.map((feature) => (
                <button
                  key={feature}
                  type="button"
                  onClick={() => toggleFeature(feature)}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                    formData.features.includes(feature)
                      ? 'bg-lyrax-primary/15 text-emerald-900 border-lyrax-primary dark:text-lyrax-primary'
                      : 'bg-lyrax-bg text-lyrax-text border-lyrax-border hover:border-lyrax-primary/50'
                  }`}
                >
                  {feature}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="text-sm font-semibold text-lyrax-text">Menú del sidebar</h2>
          <p className="mt-1 mb-4 text-sm text-lyrax-text-secondary">
            Lo que ve el tenant que usa este plan. Menú principal, Lyrax Pro y productos.
          </p>
          <SidebarMenuEditor
            value={formData.sidebarMenu}
            onChange={(sidebarMenu) => setFormData((prev) => ({ ...prev, sidebarMenu }))}
          />
        </div>
      </div>
    </form>
  );
}
